import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { googleGeocode } from '@rsos/components/capstone/common/Map/utils';
import useWhichReverseGeocodeService from '@rsos/hooks/useWhichReverseGeocodeService';
import { selectGeocodeService } from '@rsos/reduxDataStore';
import getSuggestedAddresses from '@rsos/reduxDataStore/src/features/gisSearch/helpers';
import useCenterPoint from '@rsos/utils/useCenterPoint';
import useJurisdictionBounds from './useJurisdictionBounds';

/**
 * Returns an array of verified addresses using a geocoder that corresponds to
 * the current user's access and permissions.
 *
 * + Premium users with GIS permission,  will use the GIS service provided by the agency
 *   and fall back to the Esri service if the agency service fails or no results are found.
 * + Premium users without GIS permission will use the Esri service.
 * + Non-premium users will be geocoded using Google's service.
 *
 * @param {String} address - The address to geocode
 * @returns {Array} - An array of verified addresses
 */
const usePermissionedGeocoder = (address, isSelected = false) => {
  const dispatch = useDispatch();
  const geofenceCenter = useCenterPoint();
  const service = useWhichReverseGeocodeService();
  const geocodeService = useSelector(selectGeocodeService);
  const bounds = useJurisdictionBounds();
  const [results, setResults] = useState([]);

  useEffect(() => {
    if (!address) {
      setResults([]);
    }
  }, [address]);

  useEffect(() => {
    if (address && !isSelected) {
      const geocodeWithArcgis = async searchArgs => {
        try {
          const response = await dispatch(
            getSuggestedAddresses.initiate(searchArgs),
          );
          setResults(response.data);
        } catch (error) {
          setResults([]);
        }
      };

      const geocodeWithGoogle = async searchArgs => {
        try {
          const results = await googleGeocode(
            searchArgs.address,
            searchArgs.bounds,
          );
          setResults(
            // normalize the results to match the ArcGIS format.
            results.map(result => ({
              address: result.formatted_address,
              location: {
                x: result.geometry.location.lng(),
                y: result.geometry.location.lat(),
              },
            })),
          );
        } catch (error) {
          setResults([]);
        }
      };

      if (service === 'gis' || service === 'esri') {
        geocodeWithArcgis({
          searchTerm: address,
          latitude: geofenceCenter.latitude,
          longitude: geofenceCenter.longitude,
          serviceUrl: geocodeService?.gis_service_url,
          token: geocodeService?.token || null,
        });
      } else if (service === 'google') {
        geocodeWithGoogle({ address, bounds });
      }
    } else {
      setResults(undefined);
    }

    // no need to include bounds in the dependency array because it is constant
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, address, service, isSelected, geofenceCenter, geocodeService]);

  return results;
};

export default usePermissionedGeocoder;
